import type { GoogleAutocompletePlaceItem } from 'api/search'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListSubheader from 'LuxKit/List/ListSubheader'
import usePlaceAutocomplete from 'hooks/usePlaceAutocomplete'
import noop from 'lib/function/noop'
import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'

interface Props {
  searchPhrase?: string
  onSelect?: (option: GoogleAutocompletePlaceItem) => void
  selectedPlaceId?: string
  className?: string
  originAirport: App.Airport
}

function ExperienceTransferBookingHotelSelectOptions(props: Props) {
  const {
    className,
    searchPhrase = '',
    onSelect = noop,
    selectedPlaceId,
    originAirport,
  } = props

  const intl = useIntl()

  const { isLoading, predictions } = usePlaceAutocomplete(searchPhrase, {
    latitude: originAirport.latitude,
    longitude: originAirport.longitude,
    radius: 100000,
    initialSearchTerm: 'hotel',
  })

  const noResultsFound = !isLoading && predictions.length === 0 && searchPhrase.length > 1
  const onItemSelect = (e: React.MouseEvent<HTMLButtonElement>) => {
    const placeId = e.currentTarget.dataset.placeid
    const option = predictions.find(p => p.placeId === placeId)
    onSelect(option)
  }

  return <VerticalSpacer gap={12} className={className}>
    {!isLoading && predictions.length > 0 && <div>
      {predictions.map(option => (
        <ClickableListItem
          key={option.placeId}
          selected={option.placeId === selectedPlaceId}
          data-placeid={option.placeId}
          onClick={onItemSelect}
          size="medium"
          title={option.mainText}
          subtitle={option.secondaryText}
          startIcon={<LineMapMarkerIcon />}
        />
      ))}
    </div>}
    {!isLoading && noResultsFound && <div>
      <ListSubheader colour="neutral-two">
        <FormattedMessage
          id="Fti0zUZNtE"
          defaultMessage="Sorry, we couldn’t find results for <b>{searchPhrase}</b>."
          description="Message shown when no hotel results found for the search phrase in experience transfer booking"
          values={{
            searchPhrase,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          }}
        />
      </ListSubheader>
      <ListSubheader colour="neutral-two">
        {intl.formatMessage({
          id: 'CEX9Z/8+XV',
          defaultMessage: 'Try a different search phrase',
          description: 'Suggestion to try a different search when no hotel results found in experience transfer booking',
        })}
      </ListSubheader>
    </div>}
    {isLoading && <LoadingIndicator inline />}
  </VerticalSpacer>
}

export default ExperienceTransferBookingHotelSelectOptions
